import React, { useEffect, useRef, useState } from 'react'
import { PenLine, Share2, Compass } from 'lucide-react'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { Section } from '@astryxdesign/core/Section'
import { Grid } from '@astryxdesign/core/Grid'
import { Center } from '@astryxdesign/core/Center'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import PillButton from './PillButton'
import AuthModal from './AuthModal'
import LandingHeader, { Wordmark } from './LandingHeader'
import heroImage from '../assets/landing/hero-image.png'
import heroVideo from '../assets/landing/hero-video.mp4'
import bannerImage from '../assets/landing/banner-image.png'
import footerImage from '../assets/landing/footer-image.png'

interface LandingViewProps {
  onEnter: () => void
}

interface Feature {
  icon: typeof PenLine
  title: string
  body: string
}

const FEATURES: Feature[] = [
  {
    icon: PenLine,
    title: 'Write Beautifully',
    body: 'Distraction-free editor with powerful tools to bring your ideas to life.'
  },
  {
    icon: Share2,
    title: 'Share & Connect',
    body: 'Share your stories, get feedback, and build meaningful connections with other storytellers.'
  },
  {
    icon: Compass,
    title: 'Discover New Worlds',
    body: 'Explore original stories across genres and find your next favorite adventure.'
  }
]

const FOOTER_COLUMNS: { title: string; links: string[] }[] = [
  { title: 'Product', links: ['Features', 'Editor', 'Mobile App', 'Pricing'] },
  { title: 'Community', links: ['All Stories', 'Top Writers', 'Challenges', 'Groups'] },
  { title: 'Resources', links: ['Help Center', 'Writing Guide', 'Blog', 'Status'] },
  { title: 'Company', links: ['About Us', 'Contact', 'Privacy Policy', 'Terms of Service'] }
]

const FOOTER_LINK_PATHS: Record<string, string> = {
  'Privacy Policy': '/privacy',
  'Terms of Service': '/terms'
}
export default function LandingView({ onEnter }: LandingViewProps): React.JSX.Element {
  const heroContentRef = useRef<HTMLDivElement>(null)
  const [heroMaxHeight, setHeroMaxHeight] = useState<number | null>(null)
  const [authMode, setAuthMode] = useState<'signIn' | 'signUp' | null>(null)

  useEffect(() => {
    const el = heroContentRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height
      if (height) setHeroMaxHeight(height * 2)
    })
    observer.observe(el)
    // The headline switches from the fallback serif to Playfair Display once it loads,
    // which changes its rendered height after the first ResizeObserver callback — force
    // one more measurement once web fonts are actually ready.
    document.fonts?.ready.then(() => {
      if (heroContentRef.current)
        setHeroMaxHeight(heroContentRef.current.getBoundingClientRect().height * 2)
    })
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <VStack gap={0} className="landing-page">
        <LandingHeader
          onNavClick={onEnter}
          onLogIn={() => setAuthMode('signIn')}
          onSignUp={() => setAuthMode('signUp')}
        />

        {/* Hero */}
        <Section
          variant="transparent"
          padding={0}
          className="landing-hero"
          style={
            heroMaxHeight
              ? ({ '--hero-max-height': `${heroMaxHeight}px` } as React.CSSProperties)
              : undefined
          }
        >
          <img
            src={heroImage}
            alt=""
            aria-hidden="true"
            className="landing-hero-media landing-hero-fallback"
          />
          <video
            className="landing-hero-media landing-hero-video"
            autoPlay
            loop
            muted
            playsInline
            poster={heroImage}
            aria-hidden="true"
          >
            <source src={heroVideo} type="video/mp4" />
          </video>
          <HStack className="landing-hero-row">
            <VStack gap={5} className="landing-hero-content" ref={heroContentRef}>
              <Text type="label" className="landing-eyebrow">
                Your world. Your stories.
              </Text>
              <Heading level={1} type="display-1" className="landing-heading landing-hero-heading">
                Stories worth <span className="landing-heading-accent">writing.</span>
                <br />
                Nights worth <span className="landing-heading-accent">remembering.</span>
              </Heading>
              <Text type="large" className="landing-muted">
                9nights is your space to write, explore, and share stories that inspire — from short
                tales to epic adventures.
              </Text>
              <HStack gap={3}>
                <PillButton
                  variant="primary"
                  size="lg"
                  label="Start Writing"
                  className="landing-btn-gold"
                  onClick={onEnter}
                />
                <PillButton
                  variant="secondary"
                  size="lg"
                  label="Explore Stories"
                  className="landing-btn-outline"
                  onClick={onEnter}
                />
              </HStack>
            </VStack>
          </HStack>
        </Section>

        {/* Features */}
        <Section variant="transparent" padding={8} className="landing-features-band">
          <Grid columns={3} gap={6} className="landing-features-grid">
            {FEATURES.map((feature) => (
              <VStack gap={3} key={feature.title} className="landing-feature-item">
                <Center width={48} height={48} isInline className="landing-feature-icon">
                  <feature.icon size={33} />
                </Center>
                <Heading level={3} className="landing-heading-light">
                  {feature.title}
                </Heading>
                <Text type="supporting" className="landing-muted">
                  {feature.body}
                </Text>
              </VStack>
            ))}
          </Grid>
        </Section>

        {/* CTA banner */}
        <Section variant="transparent" padding={8}>
          <HStack
            gap={10}
            className="landing-cta-band"
            style={{
              backgroundImage: `linear-gradient(90deg, rgba(11, 16, 38, 0.55), rgba(11, 16, 38, 0.35) 50%, rgba(11, 16, 38, 0.55)), url(${bannerImage})`
            }}
          >
            <VStack gap={2} className="landing-cta-content">
              <Heading level={2} className="landing-heading landing-cta-heading">
                Begin your next great story
              </Heading>
              <Text type="large" className="landing-muted landing-cta-subtext">
                Join thousands of writers and readers building worlds, one night at a time.
              </Text>
            </VStack>
            <VStack gap={2} align="center" className="landing-cta-action">
              <PillButton
                variant="primary"
                size="lg"
                label="Create Your Free Account"
                className="landing-btn-gold landing-cta-button"
                onClick={onEnter}
              />
              <Text type="supporting" className="landing-muted">
                No credit card required
              </Text>
            </VStack>
          </HStack>
        </Section>

        <Section variant="transparent" padding={0} className="landing-footer-band">
          <div
            className="landing-footer-image-cap"
            style={{
              backgroundImage: `linear-gradient(90deg, var(--nn-night) 0%, var(--nn-night) 65%, color-mix(in srgb, var(--nn-night) 92%, transparent) 75%, color-mix(in srgb, var(--nn-night) 75%, transparent) 85%, color-mix(in srgb, var(--nn-night) 45%, transparent) 93%, transparent 100%), url(${footerImage})`
            }}
          >
            <VStack gap={0} className="landing-footer-inner">
              <HStack gap={6} className="landing-footer-grid">
                <VStack gap={3} className="landing-footer-brand">
                  <Wordmark />
                  <Text type="supporting" className="landing-muted">
                    A home for storytellers and dreamers. Write, read, and share stories that last
                    beyond nine nights.
                  </Text>
                  <Text type="supporting" className="landing-muted">
                    © 2026 9nights. All rights reserved.
                  </Text>
                </VStack>
                <HStack justify="between" align="start" className="landing-footer-links">
                  {FOOTER_COLUMNS.map((column) => (
                    <VStack gap={2} key={column.title}>
                      <Text type="label" className="landing-footer-heading">
                        {column.title}
                      </Text>
                      <VStack gap={2}>
                        {column.links.map((link) => (
                          <PillButton
                            key={link}
                            variant="ghost"
                            size="sm"
                            label={link}
                            className="landing-footer-link"
                            onClick={() => {
                              const path = FOOTER_LINK_PATHS[link]
                              if (path) {
                                window.location.href = path
                              } else {
                                onEnter()
                              }
                            }}
                          />
                        ))}
                      </VStack>
                    </VStack>
                  ))}
                </HStack>
              </HStack>
            </VStack>
          </div>
        </Section>
      </VStack>
      <AuthModal
        isOpen={authMode !== null}
        onClose={() => setAuthMode(null)}
        initialMode={authMode ?? 'signIn'}
      />
    </>
  )
}
